import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { isMobile  } from 'react-device-detect';
import './GoalsMainPage.css';

// Import SVG icons
import { ReactComponent as ProfileIcon } from '../../../assets/icons/profile.svg';
import { ReactComponent as ChartIcon } from '../../../assets/icons/chart.svg';
import { ReactComponent as ArrowRightIcon } from '../../../assets/icons/arrow-right.svg';
import { ReactComponent as PlusSignIcon } from '../../../assets/icons/add.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { ReactComponent as ThreeDots } from '../../../assets/icons/three-dots.svg';
import { DefaultAvatar } from '../../../assets/images';

// Import components
import GoalComponent from '../../../components/GoalComponent';

// Import Redux actions
import GetChildrenActions from '../../../redux/GetChildrenRedux';
import CurrentSettingsActions from '../../../redux/CurrentSettingsRedux';
import ProfileBackground from '../../../assets/images/profile-background.png';
import Header from '../../../components/Header';
import MainLogo from '../../../assets/icons/cdc-logo.png';


interface GoalsMainPageProps extends WithTranslation {
  currentSettings: any;
  getChildrenFetching: boolean;
  getChildrenSuccess: boolean;
  getChildrenError: any; // Changed from boolean to any to handle error objects
  children: any[];
  getChildren: () => void;
  setCurrentChild: (child: any) => void;
  navigate?: (path: string) => void;
}

interface GoalsMainPageState {
  child: any;
  goals: any[];
  programmes: any[];
  showSwitchChildMenu: boolean;
  backButtonCount: number;
  props?: GoalsMainPageProps; // For tracking props changes
  showInfoPopup: boolean;
}

class GoalsMainPage extends Component<GoalsMainPageProps, GoalsMainPageState> {
  
  constructor(props: GoalsMainPageProps) {
    super(props);
    
    this.state = {
      child: this.props.currentSettings?.child
        ? this.props.currentSettings.child
        : this.props.children && this.props.children.length > 0
        ? this.props.children[0]
        : null,
      goals: this.props.currentSettings?.child
        ? this.props.currentSettings.child.goals
        : this.props.children && this.props.children.length > 0
        ? this.props.children[0]?.goals
        : null,
      programmes: this.props.currentSettings?.child
        ? this.props.currentSettings.child.programmes
        : this.props.children && this.props.children.length > 0
        ? this.props.children[0]?.programmes
        : null,
      showSwitchChildMenu: false,
      backButtonCount: 0,
      props: props, // Track props for comparison
      showInfoPopup: false,
    };
  }

  componentDidMount() {
    console.log('GoalsMainPage: componentDidMount');
    this.props.getChildren();
  }

  componentDidUpdate(prevProps: GoalsMainPageProps, prevState: GoalsMainPageState) {
    // Handle API response and errors FIRST
    this._handleApiResponse(prevProps);
    
    // ✅ Safe to call Redux actions here - handle side effects from state changes
    
    // Set current child when children become available for the first time
    if (!prevProps.children && this.props.children && this.props.children.length > 0) {
      if (!this.state.child) {
        this.props.setCurrentChild(this.props.children[0]);
      }
    }
    
    // Update current child when state child changes (from manual selection or data update)
    if (prevState.child !== this.state.child && this.state.child) {
      this.props.setCurrentChild(this.state.child);
    }
    
    // Handle successful children fetch - set current child based on currentSettings
    if (!prevProps.getChildrenSuccess && this.props.getChildrenSuccess && 
        this.props.children && this.props.children.length > 0) {
      const targetChildId = this.props.currentSettings?.child?.id;
      if (targetChildId) {
        const child = this.props.children.find((obj: any) => obj.id === targetChildId);
        if (child && child !== this.state.child) {
          this.setState({
            child: child,
            goals: child.goals || [],
            programmes: child.programmes || [],
          });
        }
      } else if (!this.state.child) {
        // No target child, use first available
        this.setState({
          child: this.props.children[0],
          goals: this.props.children[0]?.goals || [],
          programmes: this.props.children[0]?.programmes || [],
        });
      }
    }
  }

  // New method to handle API response and errors
  _handleApiResponse = (prevProps: GoalsMainPageProps) => {
    const { getChildrenError, getChildrenSuccess, navigate, getChildren } = this.props;
    
    // Check if we just got an error response
    if (!prevProps.getChildrenError && getChildrenError) {
      console.log('GoalsMainPage: API error occurred:', getChildrenError);
      
      // Handle both old boolean format and new detailed error format
      let errorStatus = null;
      let errorMessage = 'Unknown error occurred';
      
      if (getChildrenError === true) {
        // Old format: just boolean true
        console.log('GoalsMainPage: Legacy error format detected (boolean true)');
        console.log('GoalsMainPage: Full error object:', getChildrenError);
        console.log('GoalsMainPage: Redux state error:', this.props.getChildrenError);
        
        // Force 401 redirect for testing
        errorStatus = 401;
        errorMessage = 'Authentication error - redirecting to tutorial';
        console.log('GoalsMainPage: Forcing 401 redirect for testing');
      } else if (typeof getChildrenError === 'number') {
        errorStatus = getChildrenError;
        errorMessage = `Error ${getChildrenError} occurred`;
      } else if (getChildrenError?.status) {
        errorStatus = getChildrenError.status;
        errorMessage = getChildrenError.message || `Error ${getChildrenError.status} occurred`;
      } else if (getChildrenError?.response?.status) {
        errorStatus = getChildrenError.response.status;
        errorMessage = getChildrenError.message || `Error ${getChildrenError.response.status} occurred`;
      }
      
      console.log('GoalsMainPage: Error status extracted:', errorStatus, 'Message:', errorMessage);
      
      // Handle different error types
      if (errorStatus === 401) {
        console.log('GoalsMainPage: 401 Unauthorized - redirecting to tutorial');
        console.log('GoalsMainPage: Navigate function available:', !!navigate);
        console.log('GoalsMainPage: Navigate function:', navigate);
        
        if (navigate) {
          console.log('GoalsMainPage: Attempting to navigate to /tutorial');
          try {
            navigate('/tutorial');
            console.log('GoalsMainPage: Navigation to /tutorial completed');
          } catch (navError) {
            console.error('GoalsMainPage: Navigation error:', navError);
          }
        } else {
          console.error('GoalsMainPage: Navigate function not available!');
          // Fallback: try window.location
          console.log('GoalsMainPage: Using window.location fallback');
          window.location.href = '/tutorial';
        }
      } 
      /* else if (errorStatus === 403) {
        console.log('GoalsMainPage: 403 Forbidden - access denied');
        alert('Access denied. Please contact support.');
      } else if (errorStatus >= 500) {
        console.log('GoalsMainPage: Server error - please try again later');
        const shouldRetry = confirm('Server error occurred. Would you like to retry?');
        if (shouldRetry) {
          console.log('GoalsMainPage: Retrying API call...');
          getChildren();
        }
      } else if (errorStatus === 404) {
        console.log('GoalsMainPage: 404 Not Found - resource not available');
        alert('Children data not found. Please refresh the page.');
      } else {
        console.error('GoalsMainPage: Unknown API error:', getChildrenError);
        // Generic error handling with retry option
        const shouldRetry = confirm('An error occurred while loading data. Would you like to retry?');
        if (shouldRetry) {
          console.log('GoalsMainPage: Retrying API call...');
          getChildren();
        }
      } */
    }
    
    // Check if we just got a successful response
    if (!prevProps.getChildrenSuccess && getChildrenSuccess) {
      console.log('GoalsMainPage: API call successful, children data loaded');
    }
    
    // Check if we just started fetching (for loading states)
    if (!prevProps.getChildrenFetching && this.props.getChildrenFetching) {
      console.log('GoalsMainPage: Started fetching children data');
    }
  };

  componentWillUnmount() {
    // Cleanup if needed
  }

  shouldComponentUpdate(nextProps: GoalsMainPageProps, nextState: GoalsMainPageState) {
    // Only re-render if relevant props or state actually changed
    return (
      nextProps.children !== this.props.children ||
      nextProps.currentSettings !== this.props.currentSettings ||
      nextProps.getChildrenFetching !== this.props.getChildrenFetching ||
      nextProps.getChildrenSuccess !== this.props.getChildrenSuccess ||
      nextProps.getChildrenError !== this.props.getChildrenError ||
      nextState.child !== this.state.child ||
      nextState.goals !== this.state.goals ||
      nextState.programmes !== this.state.programmes ||
      nextState.showSwitchChildMenu !== this.state.showSwitchChildMenu ||
      nextState.showInfoPopup !== this.state.showInfoPopup
    );
  }

  _onProfilePress = () => {
    const { navigate } = this.props;
    if (navigate) {
      navigate('/settings/profile');
    }
  };

  _onStatPress = () => {
    const { navigate } = this.props;
    if (navigate) {
      navigate('/goals/progress');
    }
  };

  _onSwitchChildPress = () => {
    this.setState({ showSwitchChildMenu: true });
  };

  _onSwitchChildCancelPress = () => {
    this.setState({ showSwitchChildMenu: false });
  };

  _switchChild = (id: number) => {
    const child = this.props.children.find((obj: any) => obj.id === id);
    if (child) {
      this.props.setCurrentChild(child);
      this.setState({
        child,
        goals: child.goals,
        programmes: child.programmes,
        showSwitchChildMenu: false,
      });
    }
  };

  // Method to manually retry API call
  _retryApiCall = () => {
    console.log('GoalsMainPage: Manual retry requested');
    this.props.getChildren();
  };

  // Test method to manually trigger 401 redirect
  _test401Redirect = () => {
    console.log('GoalsMainPage: Testing 401 redirect manually');
    const { navigate } = this.props;
    
    if (navigate) {
      console.log('GoalsMainPage: Using navigate function');
      navigate('/tutorial');
    } else {
      console.log('GoalsMainPage: Using window.location fallback');
      window.location.href = '/tutorial';
    }
  };

  openInfoPopup = () => {
    this.setState({
      showInfoPopup: true,
    });
  };
  
  closeInfoPopup = () => {
    this.setState({
      showInfoPopup: false,
    });
  };

  render() {
    const { t, children, getChildrenFetching } = this.props;
    const { child, goals, showSwitchChildMenu, showInfoPopup } = this.state;

    let switchChildButton = null;
    if (children) {
      switchChildButton = (
        <div className="switch-children-bar">
          <div className="switch-children-bar-container">
            <span className="switch-children-bar-text">
              {child ? child.nickname : ""}
            </span>
            <button
              className="switch-children-bar-button"
              onClick={() => this._onSwitchChildPress()}
            >
              <ArrowRightIcon style={{ transform: 'rotate(90deg)' }} />
            </button>
          </div>
        </div>
      );
    }

    let switchChildMenu = null;
    if (showSwitchChildMenu) {
      switchChildMenu = (
        <div className="switch-child-menu-overlay" onClick={() => this._onSwitchChildCancelPress()}>
          <div className="switch-child-menu" onClick={(e) => e.stopPropagation()}>
            <h3 className="switch-child-menu-title">
              {t ? t("goals-main-screen:switch-child-menu.title") : "Switch Child"}
            </h3>
            {children && children.length
              ? children.map((childItem: any) => (
                  <button
                    key={childItem.id}
                    className={`switch-child-menu-item ${childItem.id === child?.id ? 'current' : ''}`}
                    onClick={() => this._switchChild(childItem.id)}
                  >
                    <div className="switch-child-menu-item-container">
                      <img
                        className="switch-child-menu-item-profile-pic"
                        src={
                          childItem.profile_pic
                            ? childItem.profile_pic.url
                            : DefaultAvatar
                        }
                        alt={childItem.nickname}
                      />
                      <span className="switch-child-menu-item-name">
                        {childItem.nickname}
                      </span>
                    </div>
                  </button>
                ))
              : null}
            <button
              className="switch-child-menu-cancel"
              onClick={() => this._onSwitchChildCancelPress()}
            >
              {t ? t("goals-main-screen:switch-child-menu.button.cancel") : "Cancel"}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="goals-main-page">
        {switchChildMenu}

        {/* <Header 
          title={t ? t("goals-main-screen:title") : "Goals"} 
          leftButton={
            <button className="header-button" onClick={() => this._onProfilePress()}>
              <ProfileIcon />
            </button>
          }
          rightButton={
            <button className="header-button" onClick={() => this._onStatPress()}>
              <ChartIcon />
            </button>
          }
        />*/ }
        
        <header className="page-header">
          <div className="page-header-inner">
            <div className="header-left">
              <button className="header-button" onClick={() => this._onProfilePress()}>
                <ProfileIcon />
              </button>
              <h1 className="header-title">
                  {t ? t("goals-main-screen:title") : "Goals"}
                </h1>
            </div>
            <div className="header-right">
              {isMobile && (
              <>
                <button className="header-button info-popup-button" onClick={this.openInfoPopup}>
                  <PlusSignIcon />
                </button>
               
                <div className="info-popup" style={{ display: showInfoPopup ? 'block' : 'none' }}>
                  <span className="info-popup-arrow"></span>
                  <img src={MainLogo} alt="Main Logo" className="info-popup-image" />
                  <a className="info-popup-close" onClick={this.closeInfoPopup}>
                    <CloseIcon />
                  </a>
                  <p className="info-popup-text">
                    {t ? t("goals-main-screen:info-popup.text1") : ""}
                    <ThreeDots />
                    {t ? t("goals-main-screen:info-popup.text2") : ""}
                  </p>
                </div>
              </>
              )}
               
               <button className="header-button" onClick={() => this._onStatPress()}>
                 <ChartIcon />
               </button>
             </div>
          </div>
        </header>

        {getChildrenFetching && (!this.props.children || this.props.children.length === 0) ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{t ? t("common:loading") : "Loading..."}</p>
          </div>
        ) : this.props.getChildrenError ? (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <p className="error-message">
              {t ? t("common:error-loading-data") : "Error loading data"}
            </p>
            <button 
              className="retry-button"
              onClick={this._retryApiCall}
              disabled={this.props.getChildrenFetching}
            >
              {t ? t("common:retry") : "Retry"}
            </button>
          </div>
        ) : (
          <div className="page-content">
            {switchChildButton}
            
            {children && children.length > 0 && (
              <div className="profile-container">
                <div className="profile-background">
                  <img 
                    src={ProfileBackground} 
                    alt="Profile Background" 
                    className="profile-background-image"
                    loading="lazy"
                  />
                  <div className="profile-pic-wrapper">
                    <button className="profile-pic-button" onClick={() => this._onProfilePress()}>
                      <img
                        className="profile-pic"
                        src={
                          child?.profile_pic
                            ? child.profile_pic.url
                            : DefaultAvatar
                        }
                        alt={child?.nickname || "Profile"}
                        loading="lazy"
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="goals-container">
              {goals && goals.length > 0 ? (
                <div className="goals-list">
                  {goals.map((goal: any) => (
                    <GoalComponent 
                      key={goal.id} 
                      id={goal.id} 
                      navigate={this.props.navigate}
                    />
                  ))}
                </div>
              ) : ('')}
            </div>
          </div>
        )}
      </div>
    );
  }

  static getDerivedStateFromProps(nextProps: GoalsMainPageProps, prevState: GoalsMainPageState) {
    // ✅ FIXED: No Redux actions in getDerivedStateFromProps - only pure state updates
    
    // Track props changes for componentDidUpdate
    const updates: Partial<GoalsMainPageState> = {
      props: nextProps
    };
    
    // Only update state if children data actually changed and we have no current child
    if (nextProps.children && nextProps.children.length > 0 && prevState.child === null) {
      // ❌ Removed: nextProps.setCurrentChild(nextProps.children[0]);
      return {
        ...updates,
        child: nextProps.children[0],
        goals: nextProps.children[0]?.goals || [],
        programmes: nextProps.children[0]?.programmes || [],
      };
    }

    // Check if current child data was updated in the children array
    if (nextProps.children && prevState.child !== null) {
      const updatedChild = nextProps.children.find((obj: any) => obj.id === prevState.child.id);
      
      if (updatedChild) {
        // Check if child data actually changed (avoid expensive JSON.stringify)
        const goalsChanged = updatedChild.goals !== prevState.child.goals;
        const programmesChanged = updatedChild.programmes !== prevState.child.programmes;
        
        if (goalsChanged || programmesChanged) {
          // ❌ Removed: nextProps.setCurrentChild(updatedChild);
          return {
            ...updates,
            child: updatedChild,
            goals: updatedChild.goals || [],
            programmes: updatedChild.programmes || [],
          };
        }
      } else if (nextProps.children.length > 0) {
        // Current child not found, switch to first child (state only)
        // ❌ Removed: nextProps.setCurrentChild(nextProps.children[0]);
        return {
          ...updates,
          child: nextProps.children[0],
          goals: nextProps.children[0]?.goals || [],
          programmes: nextProps.children[0]?.programmes || [],
        };
      }
    }

    // Return props update only (no state changes)
    return updates;
  }
}

const mapStateToProps = (state: any) => ({
  currentSettings: state.currentSettings,
  getChildrenFetching: state.getChildren.fetching,
  getChildrenSuccess: state.getChildren.success,
  getChildrenError: state.getChildren.error,
  children: state.getChildren.children,
});

const mapDispatchToProps = (dispatch: any) => ({
  getChildren: () => dispatch(GetChildrenActions.getChildrenRequest()),
  setCurrentChild: (child: any) => dispatch(CurrentSettingsActions.setCurrentChild(child)),
});

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(GoalsMainPage));
